import React, { useState } from "react";

import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";

import ModernModal from "../../../common/ModernModal";
import { ModernFormAlert } from "../../../common/ModernForm";
import { Episode } from "../../../Episode/services/episode.service";
import { CustomShortVideoSuggestion, CustomVideoModalStep } from "../../types";
import { videosService } from "../../services";
import { renderActions } from "./parts/Actions";
import { InputPrompt } from "./parts/InputPrompt";
import { SuggestedVideos } from "./parts/SuggestedVideos";
import { Preview } from "./parts/Preview";

interface GenerateCustomShortVideoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessfullySaved: () => void;
  episode?: Episode;
  prompt: string;
  setPrompt: (prompt: string) => void;
  hasCredits?: boolean;
  setError: (error: string) => void;
  error: string;
  generateCustomShortFormVideoIdeasMutation: any;
  currentStep: CustomVideoModalStep;
  setCurrentStep: (step: CustomVideoModalStep) => void;
  suggestedShortVideos: CustomShortVideoSuggestion[] | null;
  setSuggestedShortVideos: (
    videos: CustomShortVideoSuggestion[] | null,
  ) => void;
}

export default function GenerateCustomShortVideoModal({
  open,
  onClose,
  onSuccessfullySaved,
  episode,
  prompt,
  setPrompt,
  hasCredits = false,
  setError,
  error,
  generateCustomShortFormVideoIdeasMutation,
  currentStep,
  setCurrentStep,
  suggestedShortVideos,
  setSuggestedShortVideos,
}: GenerateCustomShortVideoModalProps) {
  const [selectedShortVideo, setSelectedShortVideo] =
    useState<CustomShortVideoSuggestion | null>(null);
  const [trimmedStart, setTrimmedStart] = useState<number>(0);
  const [trimmedEnd, setTrimmedEnd] = useState<number>(0);

  const resetModal = (): void => {
    setPrompt("");
    setSuggestedShortVideos(null);
    setSelectedShortVideo(null);
    setCurrentStep("input");
    setError("");
    setTrimmedStart(0);
    setTrimmedEnd(0);
  };

  const saveCustomShortVideoMutation = useMutation({
    mutationKey: ["save-custom-short-video"],
    mutationFn: (customShortVideo: CustomShortVideoSuggestion) =>
      videosService.saveCustomShortVideo(episode?.id!, customShortVideo),
    onSuccess: () => {
      onSuccessfullySaved();
      resetModal();
      generateCustomShortFormVideoIdeasMutation.reset();
      saveCustomShortVideoMutation.reset();
      onClose();
    },
  });

  const handleClose = (): void => {
    resetModal();
    generateCustomShortFormVideoIdeasMutation.reset();
    saveCustomShortVideoMutation.reset();
    onClose();
  };

  const handleGenerateOptions = () => {
    setError("");
    generateCustomShortFormVideoIdeasMutation.mutate({ prompt });
  };

  const handleOptionSelect = (suggestedVideo: CustomShortVideoSuggestion) => {
    setSelectedShortVideo(suggestedVideo);
    setTrimmedStart(suggestedVideo.start);
    setTrimmedEnd(suggestedVideo.end);
    setCurrentStep("preview");
  };

  const handleBackToOptions = () => {
    setCurrentStep("suggested-videos");
    setSelectedShortVideo(null);
  };

  const handleBackToInput = () => {
    setCurrentStep("input");
    setSelectedShortVideo(null);
    generateCustomShortFormVideoIdeasMutation.reset();
    saveCustomShortVideoMutation.reset();
  };

  const handleTrimChange = (start: number, end: number) => {
    setTrimmedStart(start);
    setTrimmedEnd(end);
  };

  const handleFinalSubmit = async (): Promise<void> => {
    if (!selectedShortVideo) return;

    const updatedVideo = {
      ...selectedShortVideo,
      start: trimmedStart,
      end: trimmedEnd,
      is_manual: true,
    };

    try {
      setError("");
      await saveCustomShortVideoMutation.mutateAsync(updatedVideo);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to save video. Please try again.",
      );
    }
  };

  const getModalTitle = () => {
    switch (currentStep) {
      case "input":
        return "Generate Custom Short Video";
      case "suggested-videos":
        return "Choose Video Option";
      case "preview":
        return `Preview & Edit: ${selectedShortVideo?.title || ""}`;
      default:
        return "Generate Custom Short Video";
    }
  };

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title={getModalTitle()}
      maxWidth="lg"
      actions={renderActions({
        currentStep,
        prompt,
        handleClose,
        handleGenerateOptions,
        handleBackToInput,
        handleBackToOptions,
        handleFinalSubmit,
        inProgress: generateCustomShortFormVideoIdeasMutation.isPending,
        trimmedEnd,
        trimmedStart,
        hasCredits,
      })}
      disableBackdropClick={generateCustomShortFormVideoIdeasMutation.isPending}
      disableEscapeKeyDown={generateCustomShortFormVideoIdeasMutation.isPending}
      disableCloseButton={generateCustomShortFormVideoIdeasMutation.isPending}
    >
      {error && (
        <Box sx={{ mb: 2 }}>
          <ModernFormAlert severity="error">{error}</ModernFormAlert>
        </Box>
      )}

      {!hasCredits && (
        <Box sx={{ mb: 2 }}>
          <ModernFormAlert severity="error">
            No credits available. Please upgrade your plan to generate videos.
          </ModernFormAlert>
        </Box>
      )}

      {/* Step 1: Input Prompt */}
      {currentStep === "input" && (
        <InputPrompt
          prompt={prompt}
          setPrompt={setPrompt}
          generateCustomShortFormVideoIdeasMutation={
            generateCustomShortFormVideoIdeasMutation
          }
        />
      )}

      {/* Step 2: Show Options */}
      {currentStep === "suggested-videos" && (
        <SuggestedVideos
          suggestedShortVideos={suggestedShortVideos || []}
          handleOptionSelect={handleOptionSelect}
        />
      )}

      {/* Step 3: Preview with Trimmer */}
      {currentStep === "preview" &&
        selectedShortVideo &&
        episode?.upload?.url && (
          <Preview
            selectedShortVideo={selectedShortVideo}
            episode={episode}
            handleTrimChange={handleTrimChange}
            trimmedEnd={trimmedEnd}
            trimmedStart={trimmedStart}
          />
        )}
    </ModernModal>
  );
}
